import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  InputGroup,
  InputLeftElement,
  Image,
  Textarea
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, EditIcon, DeleteIcon, ChevronDownIcon, AttachmentIcon } from '@chakra-ui/icons';
import apiService from '../../services/ApiService';
import { getCategoryImageUrl } from '../../utils/fileUtils';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isImageOpen, onOpen: onImageOpen, onClose: onImageClose } = useDisclosure();

  // ฟังก์ชันสำหรับปิด Modal และ Reset State
  const handleCloseModal = () => {
    onClose();
    setSelectedCategory(null);
    setFormData({
      title: '',
      description: '',
      link: '',
      sort_order: 0,
      is_active: true
    });
    setPreviewImage(null);
    setSelectedFile(null);
  };

  const handleCloseEditModal = () => {
    onEditClose();
    setSelectedCategory(null);
    setFormData({
      title: '',
      description: '',
      link: '',
      sort_order: 0,
      is_active: true
    });
    setPreviewImage(null);
    setSelectedFile(null);
  };

  const handleCloseImageModal = () => {
    onImageClose();
    setSelectedCategory(null);
    setSelectedFile(null);
    setPreviewImage(null);
  };
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link: '',
    sort_order: 0
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  
  const toast = useToast();

  useEffect(() => {
    fetchCategories();
  }, [currentPage, searchTerm]);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm })
      });

      const response = await apiService({
        method: 'GET',
        url: `/categories?${params}`
      });

      setCategories(response.data.categories);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('ไม่สามารถโหลดข้อมูลหมวดหมู่ได้');
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถโหลดข้อมูลหมวดหมู่ได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateCategory = () => {
    setFormData({
      title: '',
      description: '',
      link: '',
      sort_order: 0
    });
    setFormErrors({});
    setSelectedFile(null);
    setPreviewImage(null);
    onOpen();
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setFormData({
      title: category.title,
      description: category.description || '',
      link: category.link || '',
      sort_order: category.sort_order
    });
    setFormErrors({});
    setSelectedFile(null);
    setPreviewImage(category.image_url);
    onEditOpen();
  };

  const handleDeleteCategory = (category) => {
    setSelectedCategory(category);
    onDeleteOpen();
  };

  const handleImageUpload = (category) => {
    setSelectedCategory(category);
    setSelectedFile(null);
    setPreviewImage(category.image_url);
    onImageOpen();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'กรุณากรอกชื่อหมวดหมู่';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('link', formData.link);
      formDataToSend.append('sort_order', formData.sort_order);
      
      if (selectedFile) {
        formDataToSend.append('categoryImage', selectedFile);
      }

      if (selectedCategory) {
        // Update category
        await apiService({
          method: 'PUT',
          url: `/categories/${selectedCategory.id}`,
          data: formDataToSend
        });
        
        toast({
          title: 'อัปเดตหมวดหมู่สำเร็จ',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        handleCloseEditModal();
        fetchCategories();
      } else {
        // Create category
        await apiService({
          method: 'POST',
          url: '/categories',
          data: formDataToSend
        });
        
        toast({
          title: 'สร้างหมวดหมู่สำเร็จ',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        handleCloseModal();
        fetchCategories();
      }
      
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message || 'ไม่สามารถบันทึกข้อมูลได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast({
        title: 'กรุณาเลือกไฟล์รูปภาพ',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      const formDataToSend = new FormData();
      formDataToSend.append('categoryImage', selectedFile);

      await apiService({
        method: 'POST',
        url: `/categories/${selectedCategory.id}/upload-image`,
        data: formDataToSend
      });
      
      toast({
        title: 'อัปโหลดรูปภาพสำเร็จ',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onImageClose();
      fetchCategories();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message || 'ไม่สามารถอัปโหลดรูปภาพได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiService({
        method: 'DELETE',
        url: `/categories/${selectedCategory.id}`
      });
      
      toast({
        title: 'ลบหมวดหมู่สำเร็จ',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onDeleteClose();
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message || 'ไม่สามารถลบหมวดหมู่ได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const toggleCategoryStatus = async (category) => {
    try {
      await apiService({
        method: 'PUT',
        url: `/categories/${category.id}`,
        data: { is_active: !category.is_active }
      });
      
      toast({
        title: 'อัปเดตสถานะสำเร็จ',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      fetchCategories();
    } catch (error) {
      console.error('Error updating category status:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถอัปเดตสถานะได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return (
      <Center h="400px">
        <VStack>
          <Spinner size="xl" color="blue.500" />
          <Text>กำลังโหลดข้อมูล...</Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert status="error" rounded="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="2xl" fontWeight="bold">จัดการหมวดหมู่</Text>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleCreateCategory}>
          เพิ่มหมวดหมู่
        </Button>
      </Flex>

      {/* Filters */}
      <Box bg="white" p={4} rounded="lg" shadow="sm" mb={6}>
        <InputGroup maxW="300px">
          <InputLeftElement>
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="ค้นหาหมวดหมู่..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </InputGroup>
      </Box>

      {/* Categories Table */}
      <Box bg="white" rounded="lg" shadow="sm" overflow="hidden">
        <Table>
          <Thead bg="gray.50">
            <Tr>
              <Th>รูปภาพ</Th>
              <Th>ชื่อหมวดหมู่</Th>
              <Th>คำอธิบาย</Th>
              <Th>ลิงก์</Th>
              <Th>ลำดับ</Th>
              <Th>สถานะ</Th>
              <Th>จัดการ</Th>
            </Tr>
          </Thead>
          <Tbody>
            {categories.map((category) => (
              <Tr key={category.id}>
                <Td>
                  {category.image_url ? (
                    <Image
                      src={getCategoryImageUrl(category.image_url)}
                      alt={category.title}
                      boxSize="50px"
                      objectFit="cover"
                      rounded="md"
                    />
                  ) : (
                    <Box
                      boxSize="50px"
                      bg="gray.100"
                      rounded="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <AttachmentIcon color="gray.400" />
                    </Box>
                  )}
                </Td>
                <Td>
                  <Text fontWeight="medium">{category.title}</Text>
                </Td>
                <Td>
                  <Text fontSize="sm" color="gray.600" noOfLines={2}>
                    {category.description || '-'}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="sm" color="blue.500" noOfLines={1}>
                    {category.link || '-'}
                  </Text>
                </Td>
                <Td>
                  <Text>{category.sort_order}</Text>
                </Td>
                <Td>
                  <Badge 
                    colorScheme={category.is_active ? 'green' : 'red'}
                    cursor="pointer"
                    onClick={() => toggleCategoryStatus(category)}
                  >
                    {category.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                  </Badge>
                </Td>
                <Td>
                  <Menu>
                    <MenuButton as={IconButton} icon={<ChevronDownIcon />} variant="ghost" size="sm" />
                    <MenuList>
                      <MenuItem icon={<EditIcon />} onClick={() => handleEditCategory(category)}>
                        แก้ไข
                      </MenuItem>
                      <MenuItem icon={<AttachmentIcon />} onClick={() => handleImageUpload(category)}>
                        อัปโหลดรูปภาพ
                      </MenuItem>
                      <MenuItem icon={<DeleteIcon />} onClick={() => handleDeleteCategory(category)} color="red.500">
                        ลบ
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <HStack justify="center" mt={6}>
          <Button
            isDisabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            ก่อนหน้า
          </Button>
          <Text>
            หน้า {currentPage} จาก {totalPages}
          </Text>
          <Button
            isDisabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            ถัดไป
          </Button>
        </HStack>
      )}

      {/* Create/Edit Category Modal */}
      <Modal 
        isOpen={isOpen || isEditOpen} 
        onClose={selectedCategory ? handleCloseEditModal : handleCloseModal} 
        size="lg"
        closeOnOverlayClick={true}
        closeOnEsc={true}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedCategory ? 'แก้ไขหมวดหมู่' : 'เพิ่มหมวดหมู่ใหม่'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <FormControl isInvalid={formErrors.title}>
                  <FormLabel>ชื่อหมวดหมู่</FormLabel>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    placeholder="กรอกชื่อหมวดหมู่"
                  />
                  <FormErrorMessage>{formErrors.title}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>คำอธิบาย</FormLabel>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="กรอกคำอธิบาย"
                    rows={3}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>ลิงก์</FormLabel>
                  <Input
                    name="link"
                    value={formData.link}
                    onChange={handleFormChange}
                    placeholder="กรอกลิงก์ (ถ้ามี)"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>ลำดับการแสดงผล</FormLabel>
                  <Input
                    name="sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={handleFormChange}
                    placeholder="0"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>รูปภาพ</FormLabel>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  {previewImage && (
                    <Image
                      src={previewImage.startsWith('data:') ? previewImage : getCategoryImageUrl(previewImage)}
                      alt="Preview"
                      mt={2}
                      boxSize="100px"
                      objectFit="cover"
                      rounded="md"
                    />
                  )}
                </FormControl>

                <HStack w="100%" justify="flex-end">
                  <Button variant="ghost" onClick={selectedCategory ? handleCloseEditModal : handleCloseModal}>
                    ยกเลิก
                  </Button>
                  <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
                    {selectedCategory ? 'อัปเดต' : 'สร้าง'}
                  </Button>
                </HStack>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Image Upload Modal */}
      <Modal 
        isOpen={isImageOpen} 
        onClose={handleCloseImageModal}
        closeOnOverlayClick={true}
        closeOnEsc={true}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>อัปโหลดรูปภาพ - {selectedCategory?.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <form onSubmit={handleImageSubmit}>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>เลือกรูปภาพ</FormLabel>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </FormControl>

                {previewImage && (
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb={2}>ตัวอย่างรูปภาพ:</Text>
                    <Image
                      src={previewImage.startsWith('data:') ? previewImage : getCategoryImageUrl(previewImage)}
                      alt="Preview"
                      maxW="200px"
                      maxH="200px"
                      objectFit="cover"
                      rounded="md"
                    />
                  </Box>
                )}

                <HStack w="100%" justify="flex-end">
                  <Button variant="ghost" onClick={onImageClose}>
                    ยกเลิก
                  </Button>
                  <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
                    อัปโหลด
                  </Button>
                </HStack>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={isDeleteOpen} 
        onClose={onDeleteClose}
        closeOnOverlayClick={true}
        closeOnEsc={true}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ยืนยันการลบ</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text>
              คุณแน่ใจหรือไม่ที่จะลบหมวดหมู่ <strong>{selectedCategory?.title}</strong>?
              การดำเนินการนี้ไม่สามารถย้อนกลับได้
            </Text>
            <HStack mt={4} justify="flex-end">
              <Button variant="ghost" onClick={onDeleteClose}>
                ยกเลิก
              </Button>
              <Button colorScheme="red" onClick={handleDelete}>
                ลบ
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CategoryManagement;
